import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';
import {
  fetchTicketsByHour, fetchOccupancyByDayOfWeek, fetchMovieTrends,
  fetchDeadScreenings, fetchBranchEfficiency, fetchGenreStats,
  fetchBlockedSeatsStats, fetchBlockedSeatBreakdown, generateBlockedSeatDebugPack, getDebugScreenshotFileUrl,
  fetchWeeklyAnalytics,
} from '../api/client';
import { useAuth } from '../context/AuthContext';
import ChartCard from '../components/ChartCard';
import StatCard from '../components/StatCard';
import DataTable from '../components/DataTable';
import CategoryYAxisTick from '../components/CategoryYAxisTick';
import DateRangePicker, { currentIsraeliWeek } from '../components/DateRangePicker';
import {
  NEU_BG, NEU_RAISED, TEXT_PRIMARY, TEXT_SECONDARY, TEXT_FAINT, BORDER_SUBTLE,
  ACCENT, COLOR_OK, COLOR_WARN, COLOR_DANGER, COLOR_OK_BG, COLOR_DANGER_BG,
} from '../theme';

const formatNumber = (val) => (val || 0).toLocaleString();

const COLORS = ['var(--accent)', '#8b5cf6', '#f59e0b', '#22c55e', '#ec4899', '#0ea5e9', '#f97316', '#94a3b8'];

const ttp = {
  contentStyle: { background: 'var(--surf)', border: 'none', borderRadius: 12, boxShadow: 'var(--shadow)', color: 'var(--txt)', fontFamily: 'Heebo,sans-serif', fontSize: 13 },
  cursor: false,
};

function AnalyticsPage({ refreshNonce }) {
  const { hasPermission } = useAuth();
  const [hourData, setHourData] = useState([]);
  const [dayData, setDayData] = useState([]);
  const [trends, setTrends] = useState([]);
  const [deadData, setDeadData] = useState(null);
  const [efficiency, setEfficiency] = useState([]);
  const [genreData, setGenreData] = useState([]);
  const [blockedStats, setBlockedStats] = useState(null);
  const [selectedHallKey, setSelectedHallKey] = useState('');
  const [hallBreakdown, setHallBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownError, setBreakdownError] = useState('');
  const [debugPackLoading, setDebugPackLoading] = useState(false);
  const [debugPackError, setDebugPackError] = useState('');
  const [debugPack, setDebugPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weeklyData, setWeeklyData] = useState(null);
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [dateRange, setDateRange] = useState(() => currentIsraeliWeek());
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const fn = () => setWidth(window.innerWidth);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const mob = width < 768;

  useEffect(() => {
    Promise.all([
      fetchTicketsByHour(),
      fetchOccupancyByDayOfWeek(),
      fetchMovieTrends(),
      fetchDeadScreenings(10),
      fetchBranchEfficiency(),
      fetchGenreStats(),
      fetchBlockedSeatsStats(),
    ]).then(([h, d, t, dead, eff, g, blocked]) => {
      setHourData(h);
      setDayData(d);
      setTrends(t);
      setDeadData(dead);
      setEfficiency(eff);
      setGenreData(g);
      setBlockedStats(blocked);
      const halls = blocked?.halls || [];
      if (halls.length > 0) {
        const first = halls[0];
        setSelectedHallKey(`${first.cinema_id}::${first.hall}`);
      } else {
        setSelectedHallKey('');
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [refreshNonce]);

  useEffect(() => {
    setWeeklyLoading(true);
    fetchWeeklyAnalytics(null, dateRange.start, dateRange.end)
      .then(d => { setWeeklyData(d); setWeeklyLoading(false); })
      .catch(() => setWeeklyLoading(false));
  }, [dateRange, refreshNonce]);

  useEffect(() => {
    if (!selectedHallKey) {
      setHallBreakdown(null);
      setDebugPack(null);
      return;
    }
    const [cinemaId, hall] = selectedHallKey.split('::');
    if (!cinemaId || !hall) return;

    setBreakdownLoading(true);
    setBreakdownError('');
    setDebugPackError('');
    setDebugPack(null);
    fetchBlockedSeatBreakdown(cinemaId, hall)
      .then((data) => {
        if (data?.error) {
          setBreakdownError(data.error);
          setHallBreakdown(null);
        } else {
          setHallBreakdown(data);
        }
      })
      .catch(() => {
        setBreakdownError('שגיאה בטעינת פירוט האולם');
        setHallBreakdown(null);
      })
      .finally(() => setBreakdownLoading(false));
  }, [selectedHallKey]);

  const onGenerateDebugPack = () => {
    if (!selectedHallKey) return;
    const [cinemaId, hall] = selectedHallKey.split('::');
    if (!cinemaId || !hall) return;

    setDebugPackLoading(true);
    setDebugPackError('');
    generateBlockedSeatDebugPack(cinemaId, hall)
      .then((data) => {
        if (data?.error) {
          setDebugPackError(data.error);
          setDebugPack(null);
        } else {
          setDebugPack(data);
        }
      })
      .catch(() => {
        setDebugPackError('שגיאה ביצירת חבילת דיבאג');
        setDebugPack(null);
      })
      .finally(() => setDebugPackLoading(false));
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60, color: TEXT_FAINT }}>טוען ניתוחים...</div>;
  }

  const peakHour = hourData.reduce((max, h) => (h.avg_occupancy > (max?.avg_occupancy || 0) ? h : max), null);
  const bestDay = dayData.reduce((max, d) => (d.avg_occupancy > (max?.avg_occupancy || 0) ? d : max), null);
  const blockedSummary = blockedStats?.summary || null;

  const trendPct = weeklyData && weeklyData.prev_week_tickets
    ? Math.round((weeklyData.total_tickets - weeklyData.prev_week_tickets) * 100 / weeklyData.prev_week_tickets)
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* ─── Weekly Analytics Hero ─── */}
      <div>
        <h3 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 12px', color: TEXT_PRIMARY }}>ניתוח לפי טווח תאריכים</h3>
        <DateRangePicker start={dateRange.start} end={dateRange.end} onChange={setDateRange} />

        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
          <StatCard
            title="כרטיסים בטווח"
            value={weeklyLoading ? '...' : formatNumber(weeklyData?.total_tickets)}
            main
            curr={weeklyData?.total_tickets}
            prev={weeklyData?.prev_week_tickets}
          />
          <StatCard
            title="תקופה קודמת"
            value={weeklyLoading ? '...' : formatNumber(weeklyData?.prev_week_tickets)}
            subtitle={trendPct != null ? `${trendPct > 0 ? '▲' : '▼'} ${Math.abs(trendPct)}%` : undefined}
          />
          <StatCard
            title="הקרנות"
            value={formatNumber(weeklyData?.total_screenings)}
            color={COLOR_WARN}
          />
          {weeklyData?.avg_occupancy != null && (
            <StatCard
              title="תפוסה ממוצעת"
              value={`${weeklyData.avg_occupancy}%`}
              color={ACCENT}
            />
          )}
        </div>

        {weeklyData && (
          <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: 16 }}>
            {weeklyData.by_movie?.length > 0 && (
              <ChartCard title="טופ סרטים בטווח">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--surf-alt)' }}>
                      <th style={{ padding: '8px 0 8px 8px', textAlign: 'right', fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--faint)', borderBottom: '1px solid var(--brd)' }}>סרט</th>
                      <th style={{ padding: '8px 8px', textAlign: 'center', fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--faint)', borderBottom: '1px solid var(--brd)', whiteSpace: 'nowrap' }}>כרטיסים</th>
                      {!mob && <th style={{ padding: '8px 8px', textAlign: 'center', fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--faint)', borderBottom: '1px solid var(--brd)' }}>מגמה</th>}
                      {!mob && <th style={{ padding: '8px 0 8px 8px', textAlign: 'center', fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--faint)', borderBottom: '1px solid var(--brd)' }}>תפוסה</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {weeklyData.by_movie.slice(0, 10).map((r, i, arr) => {
                      const chg = r.prev_tickets ? Math.round((r.tickets - r.prev_tickets) * 100 / r.prev_tickets) : null;
                      return (
                        <tr key={i} style={{ borderBottom: i < arr.length - 1 ? '1px solid var(--brd)' : 'none', background: !r.prev_tickets ? 'rgba(99,179,237,0.08)' : undefined }}>
                          <td style={{ padding: '9px 0 9px 8px', color: 'var(--txt)', fontSize: 13, fontWeight: 500 }}>{r.title}</td>
                          <td style={{ padding: '9px 8px', textAlign: 'center', color: 'var(--accent)', fontWeight: 700, whiteSpace: 'nowrap' }}>{formatNumber(r.tickets)}</td>
                          {!mob && <td style={{ padding: '9px 8px', textAlign: 'center', fontSize: 12 }}>
                            {chg != null
                              ? (
                                <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.2 }}>
                                  <span style={{ color: chg > 0 ? COLOR_OK : COLOR_DANGER, fontWeight: 600 }}>{chg > 0 ? '▲' : '▼'} {Math.abs(chg)}%</span>
                                  <span style={{ fontSize: 9.5, color: 'var(--faint)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatNumber(r.prev_tickets)} כרטיסים</span>
                                </span>
                              )
                              : <span style={{ color: 'var(--faint)' }}>—</span>
                            }
                          </td>}
                          {!mob && <td style={{ padding: '9px 0 9px 8px', textAlign: 'center', fontSize: 12, color: 'var(--muted)' }}>
                            {r.occupancy > 0 ? `${r.occupancy}%` : '—'}
                          </td>}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </ChartCard>
            )}
            {weeklyData.by_branch?.length > 0 && (
              <ChartCard title="טופ סניפים בטווח">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--surf-alt)' }}>
                      <th style={{ padding: '8px 0 8px 8px', textAlign: 'right', fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--faint)', borderBottom: '1px solid var(--brd)' }}>סניף</th>
                      {!mob && <th style={{ padding: '8px 8px', textAlign: 'right', fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--faint)', borderBottom: '1px solid var(--brd)' }}>עיר</th>}
                      <th style={{ padding: '8px 8px', textAlign: 'center', fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--faint)', borderBottom: '1px solid var(--brd)', whiteSpace: 'nowrap' }}>כרטיסים</th>
                      {!mob && <th style={{ padding: '8px 0 8px 8px', textAlign: 'center', fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--faint)', borderBottom: '1px solid var(--brd)' }}>תפוסה</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {weeklyData.by_branch.slice(0, 10).map((r, i, arr) => (
                      <tr key={i} style={{ borderBottom: i < arr.length - 1 ? '1px solid var(--brd)' : 'none' }}>
                        <td style={{ padding: '9px 0 9px 8px', color: 'var(--txt)', fontSize: 13, fontWeight: 500 }}>{r.cinema}</td>
                        {!mob && <td style={{ padding: '9px 8px', color: 'var(--muted)', fontSize: 12 }}>{r.city}</td>}
                        <td style={{ padding: '9px 8px', textAlign: 'center', color: 'var(--accent)', fontWeight: 700, whiteSpace: 'nowrap' }}>{formatNumber(r.tickets)}</td>
                        {!mob && <td style={{ padding: '9px 0 9px 8px', textAlign: 'center', fontSize: 12, color: 'var(--muted)' }}>
                          {r.occupancy > 0 ? `${r.occupancy}%` : '—'}
                        </td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ChartCard>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1, height: 1, background: BORDER_SUBTLE }} />
        <span style={{ color: TEXT_FAINT, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>ניתוח קבוע</span>
        <div style={{ flex: 1, height: 1, background: BORDER_SUBTLE }} />
      </div>

      {/* Stats Row */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        {hasPermission('analytics.hourly_chart') && peakHour && (
          <StatCard title="שעת השיא" value={peakHour.hour_display} subtitle={`${peakHour.avg_occupancy}% תפוסה ממוצעת`} color={COLOR_WARN} />
        )}
        {hasPermission('analytics.weekly_chart') && bestDay && (
          <StatCard title="היום הכי מלא" value={`יום ${bestDay.day_name}`} subtitle={`${bestDay.avg_occupancy}% תפוסה ממוצעת`} color={COLOR_OK} />
        )}
        {hasPermission('analytics.dead_screenings') && deadData && (
          <StatCard title="הקרנות מתות (<10%)" value={`${deadData.dead_pct}%`} subtitle={`${formatNumber(deadData.dead_count)} מתוך ${formatNumber(deadData.total_screenings)}`} color={COLOR_DANGER} />
        )}
        {hasPermission('analytics.branch_efficiency') && efficiency.length > 0 && (
          <StatCard title="סניף יעיל ביותר" value={efficiency[0].cinema} subtitle={`${efficiency[0].avg_occupancy}% תפוסה • ${efficiency[0].city}`} color={ACCENT} />
        )}
        {hasPermission('analytics.blocked_seats_debug') && blockedSummary && (
          <StatCard title="כיסאות חסומים (זוהו)" value={formatNumber(blockedSummary.total_blocked_seats)} subtitle={`${formatNumber(blockedSummary.halls_tracked)} אולמות • ${formatNumber(blockedSummary.total_scans)} סריקות`} />
        )}
      </div>

      {/* Row 1: Golden Hour + Day of Week */}
      {(hasPermission('analytics.hourly_chart') || hasPermission('analytics.weekly_chart')) && (
        <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: 16 }}>
          {hasPermission('analytics.hourly_chart') && (
            <ChartCard title="שעת הזהב — תפוסה לפי שעת הקרנה">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={hourData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={BORDER_SUBTLE} />
                  <XAxis dataKey="hour_display" tick={{ fill: 'var(--txt)', fontSize: 11 }} />
                  <YAxis tick={{ fill: 'var(--faint)', fontSize: 11 }} tickFormatter={v => `${v}%`} />
                  <Tooltip {...ttp} formatter={(v, name) => [name === 'כרטיסים' ? formatNumber(v) : `${v}%`, name]} />
                  <Bar dataKey="avg_occupancy" fill={COLOR_WARN} radius={[4, 4, 0, 0]} name="תפוסה %" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {hasPermission('analytics.weekly_chart') && (
            <ChartCard title="תפוסה לפי יום בשבוע">
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={dayData} cx="50%" cy="50%" outerRadius="70%">
                  <PolarGrid stroke={BORDER_SUBTLE} />
                  <PolarAngleAxis dataKey="day_name" tick={{ fill: 'var(--txt)', fontSize: 13 }} />
                  <PolarRadiusAxis tick={{ fill: 'var(--faint)', fontSize: 10 }} />
                  <Radar name="תפוסה %" dataKey="avg_occupancy" stroke={ACCENT} fill={ACCENT} fillOpacity={0.25} />
                </RadarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </div>
      )}

      {/* Row 2: Movie Trends */}
      {hasPermission('analytics.trends') && <ChartCard title="מגמות סרטים — 3 ימים אחרונים מול 3 ימים קודמים">
        {trends.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: TEXT_FAINT }}>אין מספיק נתונים למגמות</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${BORDER_SUBTLE}` }}>
                  {['סרט', '3 ימים אחרונים', '3 ימים קודמים', 'שינוי', 'מגמה'].map((h, i) => (
                    <th key={i} style={{ padding: '10px 16px', textAlign: i === 0 ? 'right' : 'center', color: TEXT_FAINT, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {trends.map((t, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${BORDER_SUBTLE}` }}>
                    <td style={{ padding: '10px 16px', fontWeight: 500 }}>{t.title_he || t.title}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'center' }}>{formatNumber(t.recent_tickets)}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'center', color: TEXT_SECONDARY }}>{formatNumber(t.previous_tickets)}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600, color: t.trend === 'up' ? COLOR_OK : t.trend === 'down' ? COLOR_DANGER : TEXT_SECONDARY }}>
                      {t.change_pct > 0 ? '+' : ''}{t.change_pct}%
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'center', fontSize: 20 }}>
                      {t.trend === 'up' ? '📈' : t.trend === 'down' ? '📉' : '➡️'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ChartCard>}

      {/* Row 3: Genre Stats + Branch Efficiency */}
      {(hasPermission('analytics.genre_chart') || hasPermission('analytics.branch_efficiency')) && (
        <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: 16 }}>
          {hasPermission('analytics.genre_chart') && <ChartCard title="ז'אנרים — כרטיסים ותפוסה">
          {genreData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: TEXT_FAINT }}>אין נתוני ז'אנרים</div>
          ) : (
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <ResponsiveContainer width="50%" height={280}>
                <PieChart>
                  <Pie
                    data={genreData}
                    dataKey="total_tickets"
                    nameKey="genre"
                    cx="50%" cy="50%"
                    outerRadius={100}
                    label={({ genre, percent }) => `${genre} ${(percent * 100).toFixed(0)}%`}
                    labelLine={{ stroke: TEXT_FAINT }}
                  >
                    {genreData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip {...ttp} formatter={v => [formatNumber(v), 'כרטיסים']} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ flex: 1 }}>
                {genreData.slice(0, 6).map((g, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${BORDER_SUBTLE}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: COLORS[i % COLORS.length], display: 'inline-block' }} />
                      <span style={{ fontSize: 13 }}>{g.genre}</span>
                    </div>
                    <div style={{ textAlign: 'left', fontSize: 12 }}>
                      <span style={{ color: TEXT_PRIMARY }}>{formatNumber(g.total_tickets)}</span>
                      <span style={{ color: TEXT_FAINT, marginRight: 8 }}> • {g.avg_occupancy}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          </ChartCard>}

          {hasPermission('analytics.branch_efficiency') && (
            <ChartCard title="דירוג סניפים לפי יעילות">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={efficiency.slice(0, 10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke={BORDER_SUBTLE} />
                  <XAxis type="number" tick={{ fill: 'var(--faint)', fontSize: 11 }} tickFormatter={v => `${v}%`} />
                  <YAxis
                    type="category"
                    dataKey="cinema"
                    width={200}
                    tickMargin={10}
                    tick={<CategoryYAxisTick maxChars={24} fontSize={11} fill={TEXT_PRIMARY} />}
                  />
                  <Tooltip {...ttp} formatter={(v) => [`${v}%`, '']} labelFormatter={(label) => { const item = efficiency.find(e => e.cinema === label); return item ? `${label} (${item.city})` : label; }} />
                  <Bar dataKey="avg_occupancy" fill={ACCENT} fillOpacity={0.85} radius={[0, 4, 4, 0]} name="תפוסה %" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </div>
      )}

      {/* Row 4: Dead Screenings */}
      {hasPermission('analytics.dead_screenings') && deadData && deadData.screenings.length > 0 && (
        <ChartCard title={`הקרנות מתות — פחות מ-${deadData.threshold}% תפוסה (${deadData.dead_count} הקרנות)`}>
          <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${BORDER_SUBTLE}`, position: 'sticky', top: 0, background: 'var(--surf)' }}>
                  {['סרט', 'סניף', 'עיר', 'שעה', 'אולם', 'נמכרו', 'מושבים', 'תפוסה'].map((h, i) => (
                    <th key={i} style={{ padding: '10px 12px', textAlign: i > 2 ? 'center' : 'right', color: TEXT_FAINT, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {deadData.screenings.map((s, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${BORDER_SUBTLE}` }}>
                    <td style={{ padding: '8px 12px', fontSize: 13 }}>{s.movie}</td>
                    <td style={{ padding: '8px 12px', fontSize: 13 }}>{s.cinema}</td>
                    <td style={{ padding: '8px 12px', fontSize: 13, color: TEXT_SECONDARY }}>{s.city}</td>
                    <td style={{ padding: '8px 12px', fontSize: 12, textAlign: 'center', color: TEXT_SECONDARY }}>
                      {s.showtime ? new Date(s.showtime).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                    </td>
                    <td style={{ padding: '8px 12px', fontSize: 12, textAlign: 'center' }}>{s.hall}</td>
                    <td style={{ padding: '8px 12px', fontSize: 13, textAlign: 'center', fontWeight: 600, color: COLOR_DANGER }}>{s.tickets_sold}</td>
                    <td style={{ padding: '8px 12px', fontSize: 13, textAlign: 'center' }}>{s.total_seats}</td>
                    <td style={{ padding: '8px 12px', fontSize: 13, textAlign: 'center', fontWeight: 600, color: COLOR_DANGER }}>{s.occupancy}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartCard>
      )}

      {/* Row 5: Blocked seats debug panel — moved to AdminPage */}
      {false && hasPermission('analytics.blocked_seats_debug') && <ChartCard title="דיבאג מנגנון כיסאות חסומים (שלב-אחר-שלב)">
        {!blockedStats || (blockedStats.halls || []).length === 0 ? (
          <div style={{ textAlign: 'center', padding: 30, color: TEXT_FAINT }}>
            עדיין אין נתוני blocked seats להצגה.
          </div>
        ) : (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 320px', gap: 16, marginBottom: 14, alignItems: 'center' }}>
              <div style={{ fontSize: 13, color: TEXT_SECONDARY, lineHeight: 1.8 }}>
                <div><strong>1) איסוף ראיות:</strong> scans + hits לכל seat.</div>
                <div><strong>2) Confidence:</strong> יחס hits/scans + לוגיקת מנגנון.</div>
                <div><strong>3) החלטה:</strong> blocked/not blocked לפי סף.</div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: TEXT_FAINT, marginBottom: 6 }}>בחר אולם לבדיקה מיידית</label>
                <select
                  value={selectedHallKey}
                  onChange={(e) => setSelectedHallKey(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 8,
                    border: `1px solid ${BORDER_SUBTLE}`,
                    background: 'var(--surf)', color: 'var(--txt)',
                    fontFamily: 'Heebo, sans-serif',
                  }}
                >
                  {(blockedStats.halls || []).map((h, idx) => (
                    <option key={`${h.cinema_id}-${h.hall}-${idx}`} value={`${h.cinema_id}::${h.hall}`}>
                      {h.cinema} • {h.hall} • חסומים: {h.blocked_count} • סריקות: {h.scan_count}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {breakdownLoading && (
              <div style={{ padding: 20, textAlign: 'center', color: TEXT_FAINT }}>טוען פירוט אולם...</div>
            )}
            {breakdownError && (
              <div style={{ padding: 12, color: COLOR_DANGER, background: COLOR_DANGER_BG, borderRadius: 8 }}>
                {breakdownError}
              </div>
            )}

            {hallBreakdown && !breakdownLoading && (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                  <span style={debugTagStyle(ACCENT)}>אולם: {hallBreakdown.hall}</span>
                  <span style={debugTagStyle('var(--ok)')}>חסומים: {hallBreakdown.blocked_count}</span>
                  <span style={debugTagStyle('var(--muted)')}>סריקות: {hallBreakdown.scan_count}</span>
                  <span style={debugTagStyle('var(--warn)')}>גרסת מפה: {hallBreakdown.seat_map_version ?? '-'}</span>
                </div>

                <div style={{ marginBottom: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    onClick={onGenerateDebugPack}
                    disabled={debugPackLoading}
                    style={{
                      padding: '8px 12px', borderRadius: 8, border: 'none',
                      cursor: debugPackLoading ? 'default' : 'pointer',
                      background: debugPackLoading ? 'var(--faint)' : ACCENT,
                      color: '#fff', fontWeight: 700, fontSize: 12,
                    }}
                  >
                    {debugPackLoading ? 'מייצר חבילת דיבאג...' : 'צור חבילת דיבאג (Heatmap + Decision)'}
                  </button>
                  {debugPack?.seat_count ? (
                    <span style={{ fontSize: 12, color: TEXT_FAINT }}>נוצר עבור {debugPack.seat_count} מושבים</span>
                  ) : null}
                </div>

                {debugPackError ? (
                  <div style={{ marginBottom: 12, padding: 10, borderRadius: 8, background: COLOR_DANGER_BG, color: COLOR_DANGER, fontSize: 12 }}>
                    {debugPackError}
                  </div>
                ) : null}

                {debugPack ? (
                  <div style={{ marginBottom: 12, border: `1px solid ${BORDER_SUBTLE}`, borderRadius: 8, padding: 10 }}>
                    <div style={{ marginBottom: 8, fontSize: 12, color: TEXT_SECONDARY }}>קבצי דיבאג זמינים:</div>
                    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 8 }}>
                      <a href={getDebugScreenshotFileUrl(debugPack.heatmap_screenshot)} target="_blank" rel="noreferrer">Heatmap</a>
                      <a href={getDebugScreenshotFileUrl(debugPack.decision_screenshot)} target="_blank" rel="noreferrer">Decision</a>
                      <a href={getDebugScreenshotFileUrl(debugPack.source_screenshot)} target="_blank" rel="noreferrer">Source</a>
                      <a href={getDebugScreenshotFileUrl(debugPack.sidecar_json)} target="_blank" rel="noreferrer">Sidecar JSON</a>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: 8 }}>
                      <img src={getDebugScreenshotFileUrl(debugPack.heatmap_screenshot)} alt="Blocked seats heatmap overlay" style={{ width: '100%', borderRadius: 8, border: `1px solid ${BORDER_SUBTLE}` }} />
                      <img src={getDebugScreenshotFileUrl(debugPack.decision_screenshot)} alt="Blocked seats decision overlay" style={{ width: '100%', borderRadius: 8, border: `1px solid ${BORDER_SUBTLE}` }} />
                    </div>
                  </div>
                ) : null}

                <div style={{ overflowX: 'auto', maxHeight: 360, overflowY: 'auto', border: `1px solid ${BORDER_SUBTLE}`, borderRadius: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ position: 'sticky', top: 0, background: 'var(--surf)', borderBottom: `1px solid ${BORDER_SUBTLE}` }}>
                        {['Seat', 'Hits', 'Scans', 'Confidence', 'Edge', 'Decision'].map((h, i) => (
                          <th key={i} style={thStyle}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(hallBreakdown.seats || []).slice(0, 120).map((seat) => (
                        <tr key={seat.seat_id} style={{ borderBottom: `1px solid ${BORDER_SUBTLE}` }}>
                          <td style={tdStyle}>{seat.seat_label || seat.seat_id}</td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>{seat.hits}</td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>{seat.scans}</td>
                          <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700, color: seat.confidence >= 0.8 ? COLOR_OK : COLOR_WARN }}>
                            {(seat.confidence * 100).toFixed(1)}%
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>{seat.is_edge_seat ? 'כן' : 'לא'}</td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            <span style={{
                              padding: '2px 8px', borderRadius: 12,
                              background: seat.is_blocked ? 'var(--ok-bg)' : 'var(--brd)',
                              color: seat.is_blocked ? COLOR_OK : TEXT_SECONDARY,
                              fontSize: 12, fontWeight: 700,
                            }}>
                              {seat.is_blocked ? 'BLOCKED' : 'candidate'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: TEXT_FAINT }}>
                  מציג עד 120 מושבים ראשונים (מסודר לפי confidence) כדי לזהות במהירות נקודות כשל.
                </div>
              </>
            )}
          </div>
        )}
      </ChartCard>}
    </div>
  );
}

const thStyle = {
  padding: '8px 10px',
  textAlign: 'right',
  color: TEXT_FAINT,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
};

const tdStyle = {
  padding: '7px 10px',
  fontSize: 12,
};

const debugTagStyle = (color) => ({
  padding: '4px 10px',
  borderRadius: 14,
  fontSize: 12,
  fontWeight: 600,
  background: `${color}22`,
  color,
});

export default AnalyticsPage;
